import { useState } from 'react';

import { X, Check } from 'lucide-react';

import { LogicalSpeaker } from '../hooks/useTranscription';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { TranscriptSpeaker } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';

interface CorrectedSegment {
  content: string;
  speakerId: string;
  startTime: number;
  endTime: number;
}

interface DiarizationCorrectionData {
  id: string;
  confidence: number;
  originalContent: string;
  correctedSegments: CorrectedSegment[];
}

interface DiarizationCorrectionPopoverProps {
  correction: DiarizationCorrectionData;
  speakers: LogicalSpeaker[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: () => void;
  onReject: () => void;
  isProcessing?: boolean;
  children: React.ReactNode;
}

export const DiarizationCorrectionPopover = ({
  speakers,
  correction,
  isOpen,
  onOpenChange,
  onAccept,
  onReject,
  isProcessing = false,
  children,
}: DiarizationCorrectionPopoverProps) => {
  const [selectedAction, setSelectedAction] = useState<'accept' | 'reject' | null>(null);

  const handleAccept = () => {
    trackCustomEvent(TrackingEvents.AcceptDiarizationCorrection);
    setSelectedAction('accept');
    onAccept();
  };

  const handleReject = () => {
    trackCustomEvent(TrackingEvents.RejectDiarizationCorrection);
    setSelectedAction('reject');
    onReject();
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="z-50 w-[520px] p-0" side="right" align="center" sideOffset={8}>
        <div className="flex max-h-[80vh] flex-col overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Speaker Correction</h3>
            <p className="text-xs text-gray-600">Review the recommended speaker assignment</p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 text-sm font-medium text-gray-800">Recommended Correction</div>
                <div className="space-y-3">
                  {correction.correctedSegments.map((segment, index) => {
                    // Find the speaker by UUID in the transcript speakers list
                    const speaker = speakers.find((s) => s.id === segment.speakerId);
                    const speakerName = speaker?.name ?? 'Unknown Speaker';

                    const color = speaker?.color ?? 'gray';

                    return (
                      <div
                        key={index}
                        className="flex items-start gap-3 rounded-md border border-gray-100 bg-gray-50 p-3"
                      >
                        <span
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium bg-${color}-50 border-${color}-200 text-${color}-600`}
                        >
                          {speakerName}
                        </span>
                        <div className="flex-1">
                          <div className="text-sm leading-relaxed text-gray-900">{segment.content}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="flex gap-3">
              <Button onClick={handleAccept} disabled={isProcessing} variant="primary" size="sm" className="flex-1">
                <Check className="h-4 w-4" />
                Accept
              </Button>
              <Button onClick={handleReject} disabled={isProcessing} variant="outline" size="sm" className="flex-1">
                <X className="h-4 w-4" />
                Reject
              </Button>
            </div>
            {isProcessing && (
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">Processing {selectedAction}...</p>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
